import {
    ManageAccountsOutlined,
    EditOutlined,
    LocationOnOutlined,
    WorkOutlineOutlined,
    CheckOutlined,
    CloseOutlined,
  } from "@mui/icons-material";
  import { Box, Typography, Divider, useTheme, IconButton, InputBase } from "@mui/material";
  import FlexBetween from "../../components/FlexBetween";
  import WidgetWrapper from "../../components/WidgetWrapper";
  import { useSelector } from "react-redux";
  import { useEffect, useState } from "react";
  import { useNavigate } from "react-router-dom";
import UserImage from "../../components/UserImage";
import Dropzone from "react-dropzone";
  
  const UserWidget = ({ userId, picturePath }) => {
    const [user, setUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    
    const [twitterLink, setTwitterLink] = useState("");
    const [linkedinLink, setLinkedinLink] = useState("");
    const [firstNameState, setFirstNameState] = useState("");
    const [lastNameState, setLastNameState] = useState("");
    const [locationState, setLocationState] = useState("");
    const [occupationState, setOccupationState] = useState("");
    const [pictureBase64, setPictureBase64] = useState("");
    const [pictureName, setPictureName] = useState("");
    
    const { palette } = useTheme();
    const navigate = useNavigate();
    const token = useSelector((state) => state.token);
    const dark = palette.neutral.dark;
    const medium = palette.neutral.medium;
    const main = palette.neutral.main;
  
    const getUser = async () => {
      const response = await fetch(`https://dropie-service.onrender.com/users/${userId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setUser(data);
      setTwitterLink(data.twitter || "");
      setLinkedinLink(data.linkedin || "");
      setFirstNameState(data.firstName || "");
      setLastNameState(data.lastName || "");
      setLocationState(data.location || "");
      setOccupationState(data.occupation || "");
    };

    const updateProfile = async () => {
      const body = {
        twitter: twitterLink,
        linkedin: linkedinLink,
        firstName: firstNameState,
        lastName: lastNameState,
        location: locationState,
        occupation: occupationState,
      };
      if (pictureBase64) {
        body.picturePath = pictureBase64;
      }

      const response = await fetch(`https://dropie-service.onrender.com/users/${userId}/socials`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setIsEditing(false);
        setUser({...user, ...body});
        setPictureBase64("");
        setPictureName("");
        alert("Profile updated successfully!");
      }
    };

    const cancelEdit = () => {
      setIsEditing(false);
      setTwitterLink(user.twitter || "");
      setLinkedinLink(user.linkedin || "");
      setFirstNameState(user.firstName || "");
      setLastNameState(user.lastName || "");
      setLocationState(user.location || "");
      setOccupationState(user.occupation || "");
      setPictureBase64("");
      setPictureName("");
    };
  
    useEffect(() => {
      getUser();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
    if (!user) {
      return null;
    }
  
    const {
      firstName,
      lastName,
      location,
      occupation,
      viewedProfile,
      impressions,
      friends,
    } = user;
  
    return (
      <WidgetWrapper>
        {/* FIRST ROW */}
        <FlexBetween gap="0.5rem" pb="1.1rem" alignItems="flex-start">
          <FlexBetween gap="1rem" onClick={() => !isEditing && navigate(`/profile/${userId}`)} sx={{ cursor: isEditing ? "default" : "pointer" }}>
            <Box>
              <UserImage image={picturePath} />
              {isEditing && (
                <Dropzone
                  acceptedFiles=".jpg,.jpeg,.png"
                  multiple={false}
                  onDrop={(acceptedFiles) => {
                    setPictureName(acceptedFiles[0].name);
                    const reader = new FileReader();
                    reader.onload = (event) => setPictureBase64(event.target.result);
                    reader.readAsDataURL(acceptedFiles[0]);
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <Box {...getRootProps()} border={`1px dashed ${palette.primary.main}`} p="0.2rem" mt="0.5rem" sx={{ "&:hover": { cursor: "pointer" } }}>
                      <input {...getInputProps()} />
                      <Typography fontSize="0.7rem">{pictureName || "New Pic"}</Typography>
                    </Box>
                  )}
                </Dropzone>
              )}
            </Box>
            <Box>
              {isEditing ? (
                <Box display="flex" gap="0.5rem" alignItems="center">
                  <InputBase value={firstNameState} onChange={(e) => setFirstNameState(e.target.value)} sx={{ width: "80px", color: dark }} placeholder="First" />
                  <InputBase value={lastNameState} onChange={(e) => setLastNameState(e.target.value)} sx={{ width: "80px", color: dark }} placeholder="Last" />
                </Box>
              ) : (
                <Typography
                  variant="h4"
                  color={dark}
                  fontWeight="500"
                  sx={{ "&:hover": { color: palette.primary.light } }}
                >
                  {firstName} {lastName}
                </Typography>
              )}
              <Typography color={medium}>{friends.length} friends</Typography>
            </Box>
          </FlexBetween>
          
          {isEditing ? (
            <Box display="flex">
              <IconButton onClick={updateProfile} sx={{ p: "2px" }}><CheckOutlined sx={{ color: main }} /></IconButton>
              <IconButton onClick={cancelEdit} sx={{ p: "2px" }}><CloseOutlined sx={{ color: main }} /></IconButton>
            </Box>
          ) : (
            <IconButton onClick={() => setIsEditing(true)}>
              <EditOutlined sx={{ color: main }} />
            </IconButton>
          )}
        </FlexBetween>
  
        <Divider />
  
        {/* SECOND ROW */}
        <Box p="1rem 0">
          <Box display="flex" alignItems="center" gap="1rem" mb="0.5rem">
            <LocationOnOutlined fontSize="large" sx={{ color: main }} />
            {isEditing ? (
              <InputBase value={locationState} onChange={(e) => setLocationState(e.target.value)} sx={{ color: medium, width: "100%" }} placeholder="Location" />
            ) : (
              <Typography color={medium}>{location}</Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap="1rem">
            <WorkOutlineOutlined fontSize="large" sx={{ color: main }} />
            {isEditing ? (
              <InputBase value={occupationState} onChange={(e) => setOccupationState(e.target.value)} sx={{ color: medium, width: "100%" }} placeholder="Occupation" />
            ) : (
              <Typography color={medium}>{occupation}</Typography>
            )}
          </Box>
        </Box>
  
        <Divider />
  
        {/* FOURTH ROW */}
        <Box p="1rem 0">
          <Typography fontSize="1rem" color={main} fontWeight="500" mb="1rem">
            Social Profiles
          </Typography>
  
          <FlexBetween gap="1rem" mb="0.5rem">
            <FlexBetween gap="1rem" width="100%">
              <img src="../assets/twitter.png" alt="twitter" />
              <Box width="100%">
                <Typography color={main} fontWeight="500">
                  Twitter
                </Typography>
                {isEditing ? (
                  <InputBase
                    value={twitterLink}
                    onChange={(e) => setTwitterLink(e.target.value)}
                    sx={{ color: medium, width: "100%" }}
                    placeholder="Enter handle/link"
                  />
                ) : (
                  <Typography color={medium}>{twitterLink || "Social Network"}</Typography>
                )}
              </Box>
            </FlexBetween>
          </FlexBetween>
  
          <FlexBetween gap="1rem">
            <FlexBetween gap="1rem" width="100%">
              <img src="../assets/linkedin.png" alt="linkedin" />
              <Box width="100%">
                <Typography color={main} fontWeight="500">
                  Linkedin
                </Typography>
                {isEditing ? (
                  <InputBase
                    value={linkedinLink}
                    onChange={(e) => setLinkedinLink(e.target.value)}
                    sx={{ color: medium, width: "100%" }}
                    placeholder="Enter handle/link"
                  />
                ) : (
                  <Typography color={medium}>{linkedinLink || "Network Platform"}</Typography>
                )}
              </Box>
            </FlexBetween>
          </FlexBetween>
        </Box>
      </WidgetWrapper>
    );
  };
  
  export default UserWidget;
  
